import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ChevronRight, Sparkles, MapPin } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LandingPage() {
  const navigate = useNavigate();
  const [gemeinden, setGemeinden] = useState([]);
  const [showGemeinden, setShowGemeinden] = useState(false);
  const [publicSettings, setPublicSettings] = useState(null);

  useEffect(() => {
    // Fetch gemeinden and public settings
    Promise.all([
      axios.get(`${API_URL}/api/gemeinden`),
      axios.get(`${API_URL}/api/public/settings`).catch(() => ({ data: null }))
    ]).then(([gemeindenRes, settingsRes]) => {
      setGemeinden(gemeindenRes.data);
      setPublicSettings(settingsRes.data);
    }).catch(err => console.error("Error fetching data:", err));
  }, []);

  const handleGemeindeClick = (gemeinde) => {
    setShowGemeinden(false);
    navigate(`/finden?gemeinde=${gemeinde.id}&name=${encodeURIComponent(gemeinde.name)}`);
  };

  // Format date string for display
  const getEventDateDesktop = () => {
    return publicSettings?.event_dates || "24. - 28. März 2025";
  };
  
  const getEventDateMobile = () => {
    if (publicSettings?.event_dates) {
      // Replace 4-digit year with 2-digit
      return publicSettings.event_dates.replace(/(\d{4})$/, (match) => match.slice(-2));
    }
    return "24. - 28. März 25";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#FFF1F2] to-white">
      {/* Header - Simple with Logo */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center">
          <img 
            src="/logo.svg" 
            alt="Musikschule Ybbsfeld" 
            className="h-16 sm:h-20 w-auto"
          />
        </div>
      </header>

      {/* Hero Section */}
      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto overflow-visible">
          {/* Hero Content */}
          <div className="min-h-[75vh] flex flex-col items-center justify-center text-center">
            {/* Schriftzug + Date */}
            <div
              className="relative overflow-visible w-full flex flex-col items-center"
              style={{ 
                transform: 'rotate(-10.75deg)',
                transformOrigin: 'center center'
              }}
            >
              {/* Mobile: keine Kontur für bessere Lesbarkeit */}
              <motion.h1
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal leading-tight text-center
                  [text-shadow:none] sm:[text-shadow:2px_2px_0_#e3e3e3,-1px_-1px_0_#e3e3e3,1px_-1px_0_#e3e3e3,-1px_1px_0_#e3e3e3]
                  [-webkit-text-stroke:0] sm:[-webkit-text-stroke:1px_#e3e3e3]"
                style={{ 
                  fontFamily: "HandOfSean, 'Caveat Brush', cursive",
                  color: '#ef7d06'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                dangerouslySetInnerHTML={{ 
                  __html: (publicSettings?.ui_texts?.landing_slogan || 'Probiere Dein Wunschinstrument!').replace(/\n/g, '<br />') 
                }}
              />
              <motion.p
                className="text-[#8B0000] text-xl sm:text-2xl md:text-3xl lg:text-4xl text-center"
                style={{ fontFamily: "HandOfSean, 'Caveat Brush', cursive" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Mobile: short year */}
                <span className="inline sm:hidden">{getEventDateMobile()}</span>
                {/* Desktop: full year */}
                <span className="hidden sm:inline">{getEventDateDesktop()}</span>
              </motion.p>
            </div>

            <div className="h-12 md:h-16" />

            <motion.p
              className="text-lg md:text-xl text-gray-600 mb-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {publicSettings?.ui_texts?.landing_subtitle || 'Woche des offenen Unterrichts'}
            </motion.p>

            <motion.p
              className="font-accent text-2xl md:text-3xl text-[#cf2e2e] mb-10"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {publicSettings?.ui_texts?.landing_subslogan || 'Finde dein Instrument!'}
            </motion.p>

            {/* Buttons */}
            <motion.div
              className="flex flex-col items-center gap-4 w-full max-w-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* CTA Button - Los geht's - GROSS */}
              <motion.button
                data-testid="start-wizard-btn"
                onClick={() => navigate("/finden")}
                className="group relative bg-[#cf2e2e] text-white rounded-full px-10 py-6 text-xl font-bold shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 hover:bg-[#b91c1c] flex items-center justify-center gap-3"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Sparkles className="w-6 h-6" />
                <span>{publicSettings?.ui_texts?.landing_cta || "Los geht's!"}</span>
                <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                
                <motion.span
                  className="absolute inset-0 rounded-full border-2 border-[#cf2e2e]"
                  animate={{
                    scale: [1, 1.15, 1.15],
                    opacity: [0.4, 0, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                />
              </motion.button>

              {/* Standort Button - klein und flach */}
              <motion.button
                data-testid="select-standort-btn"
                onClick={() => setShowGemeinden(!showGemeinden)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  showGemeinden 
                    ? "bg-[#cf2e2e] text-white" 
                    : "bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MapPin className="w-4 h-4" />
                <span>{showGemeinden ? "Schließen" : (publicSettings?.ui_texts?.landing_location_cta || "Standort wählen")}</span>
              </motion.button>

              {/* Expanding Gemeinde Chips */}
              <AnimatePresence>
                {showGemeinden && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full overflow-hidden"
                  >
                    <motion.div 
                      className="flex flex-wrap justify-center gap-2 pt-2 pb-4"
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      variants={{
                        visible: {
                          transition: { staggerChildren: 0.04 }
                        },
                        hidden: {
                          transition: { staggerChildren: 0.02, staggerDirection: -1 }
                        }
                      }}
                    >
                      {gemeinden.map((gemeinde) => (
                        <motion.button
                          key={gemeinde.id}
                          data-testid={`gemeinde-chip-${gemeinde.id}`}
                          onClick={() => handleGemeindeClick(gemeinde)}
                          variants={{
                            hidden: { 
                              opacity: 0, 
                              scale: 0.5,
                              y: -20
                            },
                            visible: { 
                              opacity: 1, 
                              scale: 1,
                              y: 0,
                              transition: {
                                type: "spring",
                                stiffness: 400,
                                damping: 20
                              }
                            }
                          }}
                          whileHover={{ 
                            scale: 1.1, 
                            backgroundColor: "#cf2e2e",
                            color: "#ffffff",
                            transition: { duration: 0.15 }
                          }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2.5 bg-white border-2 border-gray-200 hover:border-[#cf2e2e] text-gray-700 rounded-full text-sm font-medium shadow-sm transition-colors cursor-pointer"
                        >
                          {gemeinde.name}
                        </motion.button>
                      ))}
                    </motion.div>
                    
                    {/* Subtle hint */}
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="text-xs text-gray-400 text-center"
                    >
                      Klicke auf deinen Standort
                    </motion.p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-gray-400 text-sm">
        <p>© {new Date().getFullYear()} Musikschule Ybbsfeld</p>
      </footer>
      
      {/* Click outside to close */}
      {showGemeinden && (
        <div 
          className="fixed inset-0 z-[-1]" 
          onClick={() => setShowGemeinden(false)}
        />
      )}
    </div>
  );
}
